import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { ResponsiveLine } from '@nivo/line';
import moment from 'moment';
import { tokens } from '../../theme';
import {
  fetchLtvEmails,
  fetchLtvSummary,
  LtvCohort,
  LtvEmails,
  LtvQuery,
  LtvStageMonth,
  LtvSummary,
} from '../../helpers/ftLifetimeValueData';

/**
 * Finanztrends Customer Lifetime Value — revenue per FT signup in three
 * independent N-day windows (FT from DOI, DNV from migration, KME from the
 * FT-unsubscribe import), averaged over ALL signups of the cohort month.
 * Spec: .claude/plans/2026-09-07-ft-lifetime-value-30d-windows.md
 * Doc:  .claude/docs/ft-lifetime-value-system.md
 */

const WINDOW_OPTIONS = [14, 30, 60, 90];
const DEFAULT_START = '2026-07-01';

// Mirrors ALLOWED_ANFO_SOURCES in node-app/src/helpers/anfoFilters.ts (client-scoped,
// "Finanztrends" = historic NULL default, "FT" = in-article ad units).
const SOURCE_OPTIONS = [
  'Boerse-Express',
  'FT',
  'Aktiencheck',
  'Finanztrends',
  'Ad-Hoc-News',
  'Boerse-Global',
  'Stock-World',
  'Trading-Treff',
  'Sharedeals',
  'Adhoc-FT',
  'KME',
  'Finanzennet',
  'Embed-AHN',
  'Finanzmarkt',
  'Boerse-Social',
  'APP-PUSH',
];

const ABO_STATUS_OPTIONS = [
  {
    value: 'V',
    label: 'V - Verweigert',
    description: 'Kunde blockiert (bestehendes Abo oder Kündigung < 90 Tage)',
  },
  { value: 'L', label: 'L - Laufend', description: 'Aktives Abo (Testphase oder bezahlt)' },
  {
    value: 'W',
    label: 'W - Widerrufen',
    description: 'Gekündigt oder nicht bezahlt nach Testphase',
  },
  { value: 'B', label: 'B - Beendet', description: 'Abo komplett abgelaufen' },
];

const eur = (n: number | null | undefined, digits = 2) =>
  n === null || n === undefined
    ? '–'
    : `€${n.toLocaleString('de-DE', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
const pct = (n: number | null | undefined) =>
  n === null || n === undefined
    ? '–'
    : `${(n * 100).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;
const int = (n: number) => n.toLocaleString('de-DE');
const monthLabel = (m: string) => moment(m, 'YYYY-MM').format('MMM YYYY');

const SERIES = {
  total: 'Gesamt € / Anmeldung',
  ft: 'Finanztrends € / Anmeldung',
  dnv: 'DNV € / Anmeldung',
  kme: 'KME € / Anmeldung',
};

const FtLifetimeValue: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [startDate, setStartDate] = useState<string>(DEFAULT_START);
  const [endDate, setEndDate] = useState<string>(moment().format('YYYY-MM-DD'));
  const [days, setDays] = useState<number>(30);
  const [aboStatuses, setAboStatuses] = useState<string[]>(['L', 'W', 'B']);
  const [sources, setSources] = useState<string[]>([]);

  const [summary, setSummary] = useState<LtvSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [dialogMonth, setDialogMonth] = useState<string | null>(null);
  const [emails, setEmails] = useState<LtvEmails | null>(null);
  const [emailsLoading, setEmailsLoading] = useState<boolean>(false);

  const requestSeq = useRef(0);
  const emailsSeq = useRef(0);

  const query: LtvQuery = useMemo(
    () => ({ startDate, endDate, days, aboStatuses, sources }),
    [startDate, endDate, days, aboStatuses, sources],
  );
  const queryValid =
    /^\d{4}-\d{2}-\d{2}$/.test(startDate) &&
    /^\d{4}-\d{2}-\d{2}$/.test(endDate) &&
    startDate <= endDate;

  useEffect(() => {
    if (!queryValid) return;
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    fetchLtvSummary(query)
      .then((data) => {
        if (seq !== requestSeq.current) return; // stale response
        setSummary(data);
      })
      .catch((e: unknown) => {
        if (seq !== requestSeq.current) return;
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (seq === requestSeq.current) setLoading(false);
      });
  }, [query, queryValid]);

  const openMonth = useCallback(
    (month: string) => {
      const seq = ++emailsSeq.current;
      setDialogMonth(month);
      setEmails(null);
      setEmailsLoading(true);
      fetchLtvEmails(query, month)
        .then((data) => {
          if (seq !== emailsSeq.current) return; // stale response (another row was clicked)
          setEmails(data);
        })
        .catch(() => {
          if (seq !== emailsSeq.current) return;
          setEmails(null);
        })
        .finally(() => {
          if (seq === emailsSeq.current) setEmailsLoading(false);
        });
    },
    [query],
  );

  const journeyChart = useMemo(() => {
    if (!summary) return [];
    const mk = (id: string, pick: (c: LtvCohort) => number) => ({
      id,
      data: summary.journey.map((c) => ({ x: c.month, y: Math.round(pick(c) * 100) / 100 })),
    });
    return [
      mk(SERIES.total, (c) => c.totalPerSignup),
      mk(SERIES.ft, (c) => c.ftPerSignup),
      mk(SERIES.dnv, (c) => c.dnvPerSignup),
      mk(SERIES.kme, (c) => c.kmePerSignup),
    ];
  }, [summary]);

  const stageChart = useCallback(
    (rows: LtvStageMonth[], label: string) => [
      {
        id: `${label} € / Eintritt`,
        data: rows.map((r) => ({ x: r.month, y: Math.round(r.perEntrant * 100) / 100 })),
      },
    ],
    [],
  );

  const chartTheme = {
    axis: {
      domain: { line: { stroke: colors.grey[100] } },
      legend: { text: { fill: colors.grey[100] } },
      ticks: {
        line: { stroke: colors.grey[100], strokeWidth: 1 },
        text: { fill: colors.grey[100] },
      },
    },
    legends: { text: { fill: colors.grey[100] } },
    tooltip: { container: { color: colors.primary[500] } },
  };

  const sliceTooltip = ({ slice }: { slice: { points: any[] } }) => (
    <Box
      sx={{
        background: colors.primary[400],
        p: 1.5,
        border: `1px solid ${colors.grey[100]}`,
        borderRadius: 1,
        color: colors.grey[100],
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
        {monthLabel(String(slice.points[0].data.x))}
      </Typography>
      {slice.points.map((p) => (
        <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
          <Box sx={{ width: 10, height: 10, backgroundColor: p.serieColor, borderRadius: '50%' }} />
          <Typography variant="caption" sx={{ flex: 1, mr: 1 }}>
            {p.serieId}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {eur(Number(p.data.y))}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  const renderLine = (data: any[], seriesColors: string[], legendY: string) => (
    <Box height={340}>
      <ResponsiveLine
        data={data}
        theme={chartTheme}
        colors={seriesColors}
        lineWidth={3}
        pointSize={7}
        useMesh
        enableSlices="x"
        sliceTooltip={sliceTooltip}
        margin={{ top: 30, right: 40, bottom: 80, left: 70 }}
        xScale={{ type: 'point' }}
        yScale={{ type: 'linear', min: 0, max: 'auto', stacked: false }}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: -30,
          legend: 'Monat',
          legendOffset: 55,
          legendPosition: 'middle',
          format: (v) => monthLabel(String(v)),
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickValues: 5,
          legend: legendY,
          legendOffset: -60,
          legendPosition: 'middle',
          format: (v) => eur(Number(v)),
        }}
        legends={[
          {
            anchor: 'top-left',
            direction: 'row',
            translateY: -28,
            itemWidth: 190,
            itemHeight: 18,
            symbolSize: 10,
            symbolShape: 'circle',
          },
        ]}
      />
    </Box>
  );

  const kpiCard = (title: string, value: string, sub?: string, color?: string) => (
    <Grid item xs={12} sm={6} md={2}>
      <Card sx={{ backgroundColor: colors.primary[400], height: '100%' }}>
        <CardContent>
          <Typography variant="subtitle2" color={colors.grey[300]}>
            {title}
          </Typography>
          <Typography variant="h4" color={color ?? colors.greenAccent[500]}>
            {value}
          </Typography>
          {sub && (
            <Typography variant="caption" color={colors.grey[300]}>
              {sub}
            </Typography>
          )}
        </CardContent>
      </Card>
    </Grid>
  );

  const headCell = (label: string, align: 'left' | 'right' = 'right', tip?: string) => (
    <TableCell
      align={align}
      sx={{
        backgroundColor: '#e0e0e0 !important',
        color: '#141414',
        fontWeight: 700,
        whiteSpace: 'nowrap',
      }}
    >
      {tip ? (
        <Tooltip title={tip} arrow>
          <span>{label}</span>
        </Tooltip>
      ) : (
        label
      )}
    </TableCell>
  );
  const cell = (v: React.ReactNode, align: 'left' | 'right' = 'right') => (
    <TableCell align={align} sx={{ color: '#141414', whiteSpace: 'nowrap' }}>
      {v}
    </TableCell>
  );

  const stageTable = (rows: LtvStageMonth[], label: string) => (
    <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', mt: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {headCell('Eintrittsmonat', 'left')}
            {headCell(
              'Eintritte',
              'right',
              `Anmelder der Population, die ${label} in diesem Monat erreicht haben`,
            )}
            {headCell(
              'Fenster geschlossen',
              'right',
              'Anteil der Eintritte, deren Fenster schon abgelaufen ist',
            )}
            {headCell('PLX-Orders')}
            {headCell('PLX €')}
            {headCell('VNR-Leads')}
            {headCell('VNR €')}
            {headCell('€ gesamt')}
            {headCell('€ / Eintritt')}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={9} sx={{ color: '#141414' }}>
                Keine Eintritte im gewählten Zeitraum.
              </TableCell>
            </TableRow>
          )}
          {rows.map((r) => (
            <TableRow key={r.month}>
              {cell(monthLabel(r.month), 'left')}
              {cell(int(r.entrants))}
              {cell(`${int(r.closed)} (${pct(r.closedShare)})`)}
              {cell(int(r.orders))}
              {cell(eur(r.plx))}
              {cell(int(r.leads))}
              {cell(eur(r.vnr))}
              {cell(eur(r.rev))}
              {cell(eur(r.perEntrant))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box m="20px">
      <Typography variant="h2" color={colors.grey[100]} fontWeight="bold" mb={1}>
        Wert je FT-Anmeldung — CPA-Wert und VNR-Leads je Fenster
      </Typography>
      <Typography variant="h5" color={colors.greenAccent[400]} mb={2}>
        Umsatz je E-Mail in den ersten {days} Tagen je Liste: Finanztrends → DNV →
        Kapitalmarktexperten
      </Typography>

      <Paper
        sx={{
          p: 2,
          mb: 3,
          backgroundColor: colors.blueAccent[800],
          border: `1px solid ${colors.blueAccent[600]}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <InfoOutlined sx={{ color: colors.blueAccent[400], fontSize: 24, mt: 0.5 }} />
          <Box>
            <Typography variant="h6" sx={{ color: colors.blueAccent[400], mb: 1 }}>
              So wird gerechnet
            </Typography>
            <Typography variant="body2" sx={{ color: colors.grey[100], lineHeight: 1.7 }}>
              <strong>Population:</strong> jede FT-Neuanmeldung (erstes DOI, leads_first_appearance)
              im gewählten Anmeldezeitraum, deren FT-Fenster bereits abgelaufen ist.
              <br />• <strong>Finanztrends:</strong> {days} Tage ab DOI — PLX-Bestellungen (CPA-Wert
              je Bestellung wie im CPA-Fenster), alle Bestellungen ohne DNV-/KME-Banner.
              <br />• <strong>DNV:</strong> {days} Tage ab Migration in die DNV-Liste —
              PLX-Bestellungen mit DNV-Banner + VNR-Leads aus den DNV-SAMs (je E-Mail über die
              Mailchimp-ID aufgelöst).
              <br />• <strong>KME:</strong> {days} Tage ab dem Import als FT-Abmelder in die
              KME-Liste — PLX-Bestellungen mit KME-Banner + VNR-Leads aus den KME-Shadow-SAMs.
              <br />• Jede Stufe wird über <strong>alle Anmelder</strong> des Monats gemittelt, auch
              über die, die die Stufe nie erreichen. DNV/KME laufen weiter auf, solange Fenster
              offen sind („bisher“); der Anteil geschlossener Fenster steht daneben. Nur je E-Mail
              auflösbarer Umsatz zählt — DNV-Anzeigenumsatz ohne E-Mail-Schlüssel fehlt.
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Box display="flex" flexWrap="wrap" gap={2} mb={3} alignItems="center">
        <TextField
          label="Anmeldedatum Start"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 180 }}
        />
        <TextField
          label="Anmeldedatum Ende"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 180 }}
        />
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body1" color={colors.grey[100]}>
            Fenster:
          </Typography>
          <ToggleButtonGroup
            value={days}
            exclusive
            size="small"
            onChange={(_, v) => {
              if (v !== null) setDays(v);
            }}
          >
            {WINDOW_OPTIONS.map((d) => (
              <ToggleButton key={d} value={d}>
                {d} Tage
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <FormControl sx={{ minWidth: 240 }}>
          <InputLabel id="ltv-abo-label">Abo-Status</InputLabel>
          <Select
            labelId="ltv-abo-label"
            multiple
            value={aboStatuses}
            onChange={(e) => {
              const v = e.target.value;
              setAboStatuses(typeof v === 'string' ? v.split(',') : v);
            }}
            input={<OutlinedInput label="Abo-Status" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((v) => (
                  <Chip key={v} label={v} size="small" />
                ))}
              </Box>
            )}
          >
            {ABO_STATUS_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                <Tooltip title={o.description} arrow>
                  <span>{o.label}</span>
                </Tooltip>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 220 }}>
          <InputLabel id="ltv-source-label">Quelle</InputLabel>
          <Select
            labelId="ltv-source-label"
            multiple
            value={sources}
            onChange={(e) => {
              const v = e.target.value;
              setSources(typeof v === 'string' ? v.split(',') : v);
            }}
            input={<OutlinedInput label="Quelle" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((v) => (
                  <Chip key={v} label={v} size="small" />
                ))}
              </Box>
            )}
          >
            {SOURCE_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {loading && <CircularProgress size={22} />}
      </Box>

      {error && (
        <Paper sx={{ p: 2, mb: 3, backgroundColor: colors.redAccent[700] }}>
          <Typography color="white">Fehler beim Laden: {error}</Typography>
        </Paper>
      )}
      {!queryValid && (
        <Paper sx={{ p: 2, mb: 3, backgroundColor: colors.redAccent[700] }}>
          <Typography color="white">
            Ungültiger Zeitraum (Start muss vor dem Ende liegen).
          </Typography>
        </Paper>
      )}

      {summary && (
        <>
          <Grid container spacing={2} mb={3}>
            {kpiCard(
              'Σ Anmeldungen',
              int(summary.kpis.signups),
              `FT-Fenster geschlossen (Stand ${moment(summary.today).format('DD.MM.YYYY')})`,
              colors.blueAccent[400],
            )}
            {kpiCard(
              `€ / Anmeldung (${days} Tage)`,
              eur(summary.kpis.totalPerSignup),
              `Σ ${eur(summary.kpis.totalRev)}`,
            )}
            {kpiCard(
              'FT CPA-Wert / Anmeldung',
              eur(summary.kpis.ftPerSignup),
              `Σ CPA-Wert ${eur(summary.kpis.ftRev)} — Bewertung der Anfos, kein Zahlungseingang`,
            )}
            {kpiCard(
              'DNV € / Anmeldung',
              eur(summary.kpis.dnvPerSignup),
              `${pct(summary.kpis.dnvReachedShare)} erreichen DNV · Σ ${eur(summary.kpis.dnvRev)}`,
            )}
            {kpiCard(
              'KME € / Anmeldung',
              eur(summary.kpis.kmePerSignup),
              `${pct(summary.kpis.kmeReachedShare)} erreichen KME · Σ ${eur(summary.kpis.kmeRev)}`,
            )}
            {kpiCard(
              'Fenster',
              `${days} Tage`,
              'je Liste, unabhängig voneinander',
              colors.grey[100],
            )}
          </Grid>

          <Paper elevation={3} sx={{ p: 2, mb: 3, backgroundColor: colors.primary[400] }}>
            <Typography variant="h5" color={colors.grey[100]} mb={1}>
              € je Anmeldung nach Anmeldemonat ({days}-Tage-Fenster je Liste)
            </Typography>
            {summary.journey.length === 0 ? (
              <Typography color={colors.grey[300]}>
                Keine gereiften Kohorten im Zeitraum.
              </Typography>
            ) : (
              renderLine(
                journeyChart,
                [colors.greenAccent[500], colors.blueAccent[400], '#f0a500', '#e57373'],
                '€ / Anmeldung',
              )
            )}
            <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {headCell(
                      'Anmeldemonat',
                      'left',
                      'Klick auf eine Zeile öffnet die E-Mail-Liste',
                    )}
                    {headCell('Anmeldungen')}
                    {headCell(
                      'FT CPA-Wert',
                      'right',
                      'Σ CPA-Wert der FT-Anfos im Fenster (Bewertung, kein Zahlungseingang)',
                    )}
                    {headCell('FT CPA-Wert / Anm.')}
                    {headCell(
                      'DNV erreicht',
                      'right',
                      'Anteil der Anmelder, die in die DNV-Liste migriert wurden',
                    )}
                    {headCell('DNV €', 'right', 'PLX (DNV-Banner) + VNR-Leads im DNV-Fenster')}
                    {headCell('DNV € / Anm.', 'right', 'über ALLE Anmelder des Monats')}
                    {headCell('DNV € / Erreichten')}
                    {headCell(
                      'DNV Fenster zu',
                      'right',
                      'Anteil der Erreichten mit abgelaufenem Fenster',
                    )}
                    {headCell(
                      'KME erreicht',
                      'right',
                      'Anteil der Anmelder, die als FT-Abmelder nach KME importiert wurden',
                    )}
                    {headCell('KME €', 'right', 'PLX (KME-Banner) + VNR-Leads im KME-Fenster')}
                    {headCell('KME € / Anm.', 'right', 'über ALLE Anmelder des Monats')}
                    {headCell('KME € / Erreichten')}
                    {headCell('KME Fenster zu')}
                    {headCell('Gesamt € / Anm.')}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.journey.map((c) => (
                    <TableRow
                      key={c.month}
                      hover
                      onClick={() => openMonth(c.month)}
                      sx={{ cursor: 'pointer' }}
                    >
                      {cell(monthLabel(c.month), 'left')}
                      {cell(int(c.signups))}
                      {cell(`${eur(c.ftRev)} (${int(c.ftOrders)})`)}
                      {cell(eur(c.ftPerSignup))}
                      {cell(`${int(c.dnvReached)} (${pct(c.dnvReachedShare)})`)}
                      {cell(`${eur(c.dnvRev)} (${int(c.dnvLeads)} Leads)`)}
                      {cell(eur(c.dnvPerSignup))}
                      {cell(eur(c.dnvPerReached))}
                      {cell(pct(c.dnvClosedShare))}
                      {cell(`${int(c.kmeReached)} (${pct(c.kmeReachedShare)})`)}
                      {cell(`${eur(c.kmeRev)} (${int(c.kmeLeads)} Leads)`)}
                      {cell(eur(c.kmePerSignup))}
                      {cell(eur(c.kmePerReached))}
                      {cell(pct(c.kmeClosedShare))}
                      {cell(<strong>{eur(c.totalPerSignup)}</strong>)}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} lg={6}>
              <Paper elevation={3} sx={{ p: 2, backgroundColor: colors.primary[400] }}>
                <Typography variant="h5" color={colors.grey[100]} mb={1}>
                  DNV-Stufe nach Eintrittsmonat
                </Typography>
                {summary.stages.dnv.length > 0 &&
                  renderLine(stageChart(summary.stages.dnv, 'DNV'), ['#f0a500'], '€ / Eintritt')}
                {stageTable(summary.stages.dnv, 'DNV')}
              </Paper>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Paper elevation={3} sx={{ p: 2, backgroundColor: colors.primary[400] }}>
                <Typography variant="h5" color={colors.grey[100]} mb={1}>
                  KME-Stufe nach Eintrittsmonat
                </Typography>
                {summary.stages.kme.length > 0 &&
                  renderLine(stageChart(summary.stages.kme, 'KME'), ['#e57373'], '€ / Eintritt')}
                {stageTable(summary.stages.kme, 'KME')}
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      <Dialog
        open={dialogMonth !== null}
        onClose={() => setDialogMonth(null)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { backgroundColor: '#ffffff', color: '#141414' } }}
      >
        <DialogTitle>
          <Typography variant="h4" sx={{ color: '#141414' }}>
            E-Mails der Kohorte {dialogMonth ? monthLabel(dialogMonth) : ''}
            {emails &&
              ` — ${int(emails.totalCount)} Anmelder${emails.truncated ? ' (gekürzt)' : ''}`}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {emailsLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="200px">
              <CircularProgress />
            </Box>
          ) : emails ? (
            <TableContainer
              component={Paper}
              sx={{ mt: 1, backgroundColor: '#ffffff', maxHeight: '70vh' }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {headCell('E-Mail', 'left')}
                    {headCell('Anmeldung (DOI)')}
                    {headCell('DNV-Migration')}
                    {headCell(
                      'KME-Import',
                      'right',
                      'Importtag; Tooltip zeigt die FT-Abmeldung davor',
                    )}
                    {headCell('FT € (Orders)')}
                    {headCell('DNV PLX €')}
                    {headCell('DNV VNR € (Leads)')}
                    {headCell('KME PLX €')}
                    {headCell('KME VNR € (Leads)')}
                    {headCell('Gesamt €')}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {emails.rows.map((r) => (
                    <TableRow key={r.email}>
                      {cell(r.email, 'left')}
                      {cell(moment(r.signupDate).format('DD.MM.YYYY'))}
                      {cell(r.dnvDate ? moment(r.dnvDate).format('DD.MM.YYYY') : '–')}
                      {cell(
                        r.kmeDate ? (
                          <Tooltip
                            title={
                              r.kmeUnsubTs
                                ? `FT-Abmeldung ${moment.utc(r.kmeUnsubTs).format('DD.MM.YYYY HH:mm')} UTC`
                                : ''
                            }
                            arrow
                          >
                            <span>{moment(r.kmeDate).format('DD.MM.YYYY')}</span>
                          </Tooltip>
                        ) : (
                          '–'
                        ),
                      )}
                      {cell(`${eur(r.ftRev)} (${r.ftOrders})`)}
                      {cell(eur(r.dnvPlx))}
                      {cell(`${eur(r.dnvVnr)} (${r.dnvLeads})`)}
                      {cell(eur(r.kmePlx))}
                      {cell(`${eur(r.kmeVnr)} (${r.kmeLeads})`)}
                      {cell(<strong>{eur(r.totalRev)}</strong>)}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography sx={{ color: '#141414' }}>Keine Daten.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogMonth(null)} sx={{ color: '#141414' }}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FtLifetimeValue;
